import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { TRIP_DETAILS } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
    Plane,
    PlaneTakeoff,
    PlaneLanding,
    Hotel,
    ChevronDown,
    Loader2,
    Check,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const EMPTY = {
    arrival_date: '',
    arrival_time: '',
    arrival_airport: '',
    arrival_flight: '',
    departure_date: '',
    departure_time: '',
    departure_airport: '',
    departure_flight: '',
    hotel_name: '',
    hotel_address: '',
    checkin_time: '15:00',
    checkout_time: '11:00',
};

function fmtTime(t) {
    if (!t || !/^\d{2}:\d{2}$/.test(t)) return null;
    const [hh, mm] = t.split(':').map(Number);
    const period = hh >= 12 ? 'PM' : 'AM';
    const hour12 = hh % 12 || 12;
    return mm === 0 ? `${hour12}:${String(mm).padStart(2, '0')} ${period}` : `${hour12}:${String(mm).padStart(2, '0')} ${period}`;
}

function fmtDate(d) {
    if (!d) return null;
    try { return format(parseISO(d), 'd MMM'); } catch { return null; }
}

function buildSummary(d) {
    if (!d) return null;
    const parts = [];
    const flight = d.arrival_flight?.trim();
    const time = fmtTime(d.arrival_time);
    const date = fmtDate(d.arrival_date);
    if (flight || time || date) {
        const bits = [];
        if (flight) bits.push(flight);
        if (date || time) bits.push(`arriving ${[date, time].filter(Boolean).join(' ')}`.trim());
        parts.push(bits.filter(Boolean).join(' '));
    }
    if (d.hotel_name?.trim()) parts.push(d.hotel_name.trim());
    if (d.checkin_time) {
        const t = fmtTime(d.checkin_time);
        if (t) parts.push(`Check-in ${t}`);
    }
    return parts.length ? parts.join(' · ') : null;
}

function FieldLabel({ children }) {
    return (
        <label className="text-[10px] uppercase tracking-[0.18em] text-[var(--ns-text3)] block mb-1.5">{children}</label>
    );
}

export default function TripDetailsSection({ tripId }) {
    const [details, setDetails] = useState(EMPTY);
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [justSaved, setJustSaved] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/trips/${tripId}/details`);
                if (cancelled) return;
                const d = data?.trip_details || data?.details || {};
                setDetails({ ...EMPTY, ...d, checkin_time: d.checkin_time || '15:00', checkout_time: d.checkout_time || '11:00' });
                // Open by default when there is no data yet so the user notices it
                const hasAny = Object.entries(d || {}).some(([k, v]) => k !== 'checkin_time' && k !== 'checkout_time' && !!v);
                setOpen(!hasAny);
            } catch {
                // fall back to defaults
                if (!cancelled) setDetails(EMPTY);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [tripId]);

    const setField = (key, value) => setDetails(prev => ({ ...prev, [key]: value }));

    const save = useCallback(async (next) => {
        const payload = next || details;
        setSaving(true);
        try {
            await api.post(`/trips/${tripId}/details`, payload);
            setJustSaved(true);
            setTimeout(() => setJustSaved(false), 1500);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not save trip details');
        } finally {
            setSaving(false);
        }
    }, [details, tripId]);

    const blurSave = () => { if (loaded) save(); };

    const summary = buildSummary(details);

    return (
        <section
            data-testid={TRIP_DETAILS.section}
            className="ns-card mt-6 overflow-hidden"
        >
            <Collapsible open={open} onOpenChange={setOpen}>
                <CollapsibleTrigger asChild>
                    <button
                        type="button"
                        data-testid={TRIP_DETAILS.toggleButton}
                        className="w-full flex items-center gap-4 px-5 sm:px-6 py-4 text-left hover:bg-[var(--ns-soft)] transition"
                    >
                        <span
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: 'var(--ns-soft)' }}
                        >
                            <Plane size={16} className="text-[var(--ns-clay)]" />
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ns-text3)]">Trip details</div>
                            {summary ? (
                                <div
                                    data-testid={TRIP_DETAILS.summary}
                                    className="text-sm sm:text-base text-[var(--ns-text)] font-medium truncate mt-0.5"
                                >
                                    {summary}
                                </div>
                            ) : (
                                <div
                                    data-testid={TRIP_DETAILS.summary}
                                    className="text-sm text-[var(--ns-text3)] italic mt-0.5"
                                >
                                    Add flight numbers, hotel and check-in times
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {saving && (
                                <span data-testid={TRIP_DETAILS.savingIndicator} className="inline-flex items-center gap-1 text-xs text-[var(--ns-text3)]">
                                    <Loader2 size={12} className="animate-spin" /> Saving
                                </span>
                            )}
                            {!saving && justSaved && (
                                <span className="inline-flex items-center gap-1 text-xs text-[var(--ns-success,#3F8E5B)]">
                                    <Check size={12} /> Saved
                                </span>
                            )}
                            <ChevronDown
                                size={18}
                                className="text-[var(--ns-text3)] transition-transform"
                                style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
                            />
                        </div>
                    </button>
                </CollapsibleTrigger>

                <CollapsibleContent>
                    <div data-testid={TRIP_DETAILS.body} className="px-5 sm:px-6 pb-6 pt-1 border-t border-[var(--ns-border)] space-y-6">
                        {/* Flights */}
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mt-5 mb-3 flex items-center gap-1.5">
                                <PlaneTakeoff size={12} /> Flights
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                {/* Arrival */}
                                <div className="rounded-2xl border border-[var(--ns-border)] bg-white p-4 sm:p-5 space-y-3">
                                    <div className="flex items-center gap-2 text-[var(--ns-clay)] font-display font-semibold tracking-tight">
                                        <PlaneLanding size={14} /> Arrival
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <FieldLabel>Date</FieldLabel>
                                            <Input
                                                type="date"
                                                value={details.arrival_date || ''}
                                                onChange={(e) => setField('arrival_date', e.target.value)}
                                                onBlur={blurSave}
                                                data-testid={TRIP_DETAILS.arrivalDate}
                                                className="rounded-xl border-[var(--ns-border)] bg-white h-10"
                                            />
                                        </div>
                                        <div>
                                            <FieldLabel>Time</FieldLabel>
                                            <Input
                                                type="time"
                                                value={details.arrival_time || ''}
                                                onChange={(e) => setField('arrival_time', e.target.value)}
                                                onBlur={blurSave}
                                                data-testid={TRIP_DETAILS.arrivalTime}
                                                className="rounded-xl border-[var(--ns-border)] bg-white h-10"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <FieldLabel>Airport / Station</FieldLabel>
                                        <Input
                                            value={details.arrival_airport || ''}
                                            onChange={(e) => setField('arrival_airport', e.target.value)}
                                            onBlur={blurSave}
                                            placeholder="e.g. SIN, Changi T3"
                                            maxLength={60}
                                            data-testid={TRIP_DETAILS.arrivalAirport}
                                            className="rounded-xl border-[var(--ns-border)] bg-white h-10"
                                        />
                                    </div>
                                    <div>
                                        <FieldLabel>Flight number</FieldLabel>
                                        <Input
                                            value={details.arrival_flight || ''}
                                            onChange={(e) => setField('arrival_flight', e.target.value)}
                                            onBlur={blurSave}
                                            placeholder="e.g. SQ 52"
                                            maxLength={20}
                                            data-testid={TRIP_DETAILS.arrivalFlight}
                                            className="rounded-xl border-[var(--ns-border)] bg-white h-10 uppercase"
                                        />
                                    </div>
                                </div>

                                {/* Departure */}
                                <div className="rounded-2xl border border-[var(--ns-border)] bg-white p-4 sm:p-5 space-y-3">
                                    <div className="flex items-center gap-2 text-[var(--ns-clay)] font-display font-semibold tracking-tight">
                                        <PlaneTakeoff size={14} /> Departure
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <FieldLabel>Date</FieldLabel>
                                            <Input
                                                type="date"
                                                value={details.departure_date || ''}
                                                onChange={(e) => setField('departure_date', e.target.value)}
                                                onBlur={blurSave}
                                                data-testid={TRIP_DETAILS.departureDate}
                                                className="rounded-xl border-[var(--ns-border)] bg-white h-10"
                                            />
                                        </div>
                                        <div>
                                            <FieldLabel>Time</FieldLabel>
                                            <Input
                                                type="time"
                                                value={details.departure_time || ''}
                                                onChange={(e) => setField('departure_time', e.target.value)}
                                                onBlur={blurSave}
                                                data-testid={TRIP_DETAILS.departureTime}
                                                className="rounded-xl border-[var(--ns-border)] bg-white h-10"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <FieldLabel>Airport / Station</FieldLabel>
                                        <Input
                                            value={details.departure_airport || ''}
                                            onChange={(e) => setField('departure_airport', e.target.value)}
                                            onBlur={blurSave}
                                            placeholder="e.g. CDG, Gare du Nord"
                                            maxLength={60}
                                            data-testid={TRIP_DETAILS.departureAirport}
                                            className="rounded-xl border-[var(--ns-border)] bg-white h-10"
                                        />
                                    </div>
                                    <div>
                                        <FieldLabel>Flight number</FieldLabel>
                                        <Input
                                            value={details.departure_flight || ''}
                                            onChange={(e) => setField('departure_flight', e.target.value)}
                                            onBlur={blurSave}
                                            placeholder="e.g. AF 257"
                                            maxLength={20}
                                            data-testid={TRIP_DETAILS.departureFlight}
                                            className="rounded-xl border-[var(--ns-border)] bg-white h-10 uppercase"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Accommodation */}
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)] mb-3 flex items-center gap-1.5">
                                <Hotel size={12} /> Accommodation
                            </div>
                            <div className="rounded-2xl border border-[var(--ns-border)] bg-white p-4 sm:p-5 space-y-3">
                                <div>
                                    <FieldLabel>Hotel / Property name</FieldLabel>
                                    <Input
                                        value={details.hotel_name || ''}
                                        onChange={(e) => setField('hotel_name', e.target.value)}
                                        onBlur={blurSave}
                                        placeholder="e.g. Hotel Grand Hyatt"
                                        maxLength={140}
                                        data-testid={TRIP_DETAILS.hotelName}
                                        className="rounded-xl border-[var(--ns-border)] bg-white h-10"
                                    />
                                </div>
                                <div>
                                    <FieldLabel>Address</FieldLabel>
                                    <Input
                                        value={details.hotel_address || ''}
                                        onChange={(e) => setField('hotel_address', e.target.value)}
                                        onBlur={blurSave}
                                        placeholder="Street, city, postcode"
                                        maxLength={200}
                                        data-testid={TRIP_DETAILS.hotelAddress}
                                        className="rounded-xl border-[var(--ns-border)] bg-white h-10"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <FieldLabel>Check-in time</FieldLabel>
                                        <Input
                                            type="time"
                                            value={details.checkin_time || '15:00'}
                                            onChange={(e) => setField('checkin_time', e.target.value)}
                                            onBlur={blurSave}
                                            data-testid={TRIP_DETAILS.checkinTime}
                                            className="rounded-xl border-[var(--ns-border)] bg-white h-10"
                                        />
                                    </div>
                                    <div>
                                        <FieldLabel>Check-out time</FieldLabel>
                                        <Input
                                            type="time"
                                            value={details.checkout_time || '11:00'}
                                            onChange={(e) => setField('checkout_time', e.target.value)}
                                            onBlur={blurSave}
                                            data-testid={TRIP_DETAILS.checkoutTime}
                                            className="rounded-xl border-[var(--ns-border)] bg-white h-10"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <p className="text-xs text-[var(--ns-text3)] -mt-2">
                            Changes save automatically when you click away from a field.
                        </p>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </section>
    );
}
